import { useMutation } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

type MutationShareDeliveryNoteArguments = {
  assetId: UUID;
  shareUserId: UUID;
};

type ShareDeliveryNoteResponse = {
  code: string;
  message: string;
  requestId: string;
};

/**
 * Shares a delivery note with a user.
 *
 * @param {string} assetId - The asset ID of the delivery note.
 * @param {Object} shareUserId - The user ID of the delivery note to share.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/share_dlns_asset__asset_id__share_post
 */

export const shareDeliveryNote = async (assetId: UUID, shareUserId: UUID) => {
  const response = await vestigasApi
    .post(ENDPOINT.DELIVERY_NOTE.SHARE(assetId), {
      searchParams: { shareUserId },
    })
    .json();

  return response;
};

/**
 * Custom hook for share delivery note.
 *
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationShareDeliveryNote = (
  options?: Parameters<typeof useMutation>[0],
) => {
  return useMutation({
    mutationFn: async ({
      assetId,
      shareUserId,
    }: MutationShareDeliveryNoteArguments) =>
      shareDeliveryNote(assetId, shareUserId),
    ...options,
  });
};
